import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudentProfileStore } from '../store/studentProfileStore';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { motion } from 'framer-motion';
import AuraSummary from '../components/AuraSummary';
import ProfileUpdate from '../components/ProfileUpdate';

const categories = [
  {
    name: 'Participation Comfort',
    description: 'Your comfort level with different types of class participation',
    questions: [
      'I feel comfortable speaking up in large group discussions',
      'I prefer participating in small group activities',
      'I am confident asking questions during class',
      'I am comfortable presenting in front of the class'
    ],
    type: 'likert'
  },
  {
    name: 'Learning Environment',
    description: 'Your preferences for different learning environments',
    questions: [
      'I work well in collaborative group settings',
      'I prefer structured, organized learning environments',
      'I adapt easily to changes in teaching methods',
      'I engage better in interactive learning activities'
    ],
    type: 'likert'
  },
  {
    name: 'Communication Style',
    description: 'Your preferred methods of communication in class',
    questions: [
      'I communicate effectively through written assignments',
      'I express my ideas clearly in verbal discussions',
      'I am comfortable with peer-to-peer communication',
      'I actively participate in online discussions'
    ],
    type: 'likert'
  }
];

const likertOptions = [
  'Strongly Disagree',
  'Disagree',
  'Neutral',
  'Agree',
  'Strongly Agree'
];

const introSteps = [
  {
    title: "Welcome to Your Learning Journey!",
    description: "We're about to create your unique Learning Color Aura that will enhance your educational experience.",
    icon: "🎓"
  },
  {
    title: "Discover Your Learning Color Aura",
    description: "Through a series of quick surveys, we'll map your characteristics across different dimensions to create your personalized Learning Color Aura pattern.",
    icon: "✨"
  },
  {
    title: "Connect with Your Classmates",
    description: "Find study partners with complementary Learning Color Auras and similar learning styles for optimal collaboration and growth.",
    icon: "🤝"
  },
  {
    title: "Personalized Learning Experience",
    description: "Help your professors understand your unique Learning Color Aura to optimize your educational journey.",
    icon: "🎯"
  },
  {
    title: "Ready to Begin?",
    description: "Let's start building your Learning Color Aura! Your responses will help create a more engaging and effective learning environment.",
    icon: "🚀"
  }
];

const StudentDashboard = () => {
  const { classId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profile, setResponse, nextStep, previousStep, setClassId, setUserId, saveProfile, loadProfile } = useStudentProfileStore();
  const [responses, setResponses] = useState<string[]>(Array(4).fill(''));
  const [showIntro, setShowIntro] = useState(true);
  const [introStep, setIntroStep] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dashboard navigation state
  const [currentView, setCurrentView] = useState<'profile' | 'enrollment' | 'class-selection' | 'survey'>('profile');
  const [availableClasses, setAvailableClasses] = useState<any[]>([]);
  const [enrolledClasses, setEnrolledClasses] = useState<any[]>([]);
  const [selectedClassesForEnrollment, setSelectedClassesForEnrollment] = useState<string[]>([]);
  const [selectedClassForSurvey, setSelectedClassForSurvey] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showProfileUpdate, setShowProfileUpdate] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string>('');

  useEffect(() => {
    const initializeProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setUserId(user.id);

      try {
        // Ensure user profile exists
        await apiService.createUserProfile({
          id: user.id,
          email: user.email || '',
          user_type: 'student',
          full_name: user.user_metadata?.full_name || 'Student'
        });

        // Load available classes and student's enrolled classes
        const [allClassesResult, enrolledClassesResult] = await Promise.all([
          apiService.getAllAvailableClasses(), // Get all classes with professor info
          apiService.getStudentEnrollments(user.id)
        ]);


        setAvailableClasses(allClassesResult.data || []);
        setEnrolledClasses(enrolledClassesResult.data || []);

        // If classId is provided in URL, go directly to survey for that class
        if (classId) {
          const isEnrolled = enrolledClassesResult.data?.some(
            (enrollment: any) => enrollment?.class?.id === classId
          );
          
          if (isEnrolled) {
            setSelectedClassForSurvey(classId);
            setClassId(classId);
            setCurrentView('survey');
            
            // Load existing profile
            await loadProfile(user.id, classId);
            
            if (profile.currentStep > 1 && !isEditMode) {
              setShowIntro(false);
            }
            
            if (profile.currentStep >= categories.length && !isEditMode) {
              setShowSummary(true);
            }
          } else {
            // Student not enrolled in this class, stay on profile view
            setCurrentView('profile');
          }
        } else {
          // No specific class requested, default to profile view
          setCurrentView('profile');
        }
      } catch (error) {
        console.error('Initialization error:', error);
        setCurrentView('profile');
      } finally {
        setLoading(false);
      }
    };

    initializeProfile();
  }, [user, classId, loadProfile, setUserId, setClassId, profile.currentStep]);

  const handleNext = async () => {
    const currentCategory = categories[profile.currentStep - 1];
    setResponse(currentCategory.name.toLowerCase().replace(/\s+/g, '') as any, responses);
    
    if (profile.currentStep === categories.length) {
      await handleSurveyComplete();
    } else {
      setResponses(Array(4).fill(''));
      nextStep();
      await saveProfile();
    }
  };

  const handlePrevious = () => {
    previousStep();
    setResponses(Array(4).fill(''));
  };

  const handleIntroNext = () => {
    if (introStep < introSteps.length - 1) {
      setIntroStep(introStep + 1);
    } else {
      setShowIntro(false);
      // If in edit mode and survey is complete, start from step 1
      if (isEditMode && profile.currentStep >= categories.length) {
        // Reset to first step but keep responses
        profile.currentStep = 1;
      }
    }
  };

  const handleEnrollmentSubmit = async () => {
    if (selectedClassesForEnrollment.length === 0) return;
    
    setEnrollmentLoading(true);
    setEnrollmentError('');
    
    try {
      // Enroll student in selected classes
      await Promise.all(selectedClassesForEnrollment.map(classId => 
        apiService.enrollStudent({
          class_id: classId,
          student_id: user!.id,
          status: 'active'
        })
      ));
      
      // Refresh enrolled classes
      const { data: updatedEnrollments } = await apiService.getStudentEnrollments(user!.id);
      setEnrolledClasses(updatedEnrollments || []);
      
      // Clear selected classes
      setSelectedClassesForEnrollment([]);
      
      // Return to main dashboard
      setCurrentView('profile');
    } catch (error) {
      console.error('Enrollment error:', error);
      setEnrollmentError('Failed to enroll in classes. Please try again.');
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handleClassSelectionForSurvey = async () => {
    if (!selectedClassForSurvey) return;
    
    setClassId(selectedClassForSurvey);
    setCurrentView('survey');
    
    try {
      // Load existing profile for the selected class
      await loadProfile(user!.id, selectedClassForSurvey);
      
      if (profile.currentStep > 1 && !isEditMode) {
        setShowIntro(false);
      }
      
      if (profile.currentStep >= categories.length && !isEditMode) {
        setShowSummary(true);
      }
    } catch (error) {
      console.error('Survey loading error:', error);
    }
  };

  const handleEditSurvey = () => {
    setIsEditMode(true);
    setShowSummary(false);
    setShowIntro(true);
    setIntroStep(0);
  };

  const handleSurveyComplete = async () => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    setShowSummary(true);
    await saveProfile();
  };

  const handleBackToClassSelection = () => {
    setCurrentView('profile');
    setSelectedClassForSurvey('');
    setShowIntro(true);
    setShowSummary(false);
    setIsEditMode(false);
  };


  const handleProfileUpdateSave = () => {
    setShowProfileUpdate(false);
    // Could add a success message or refresh data here
  };

  // Load existing responses when navigating through steps
  useEffect(() => {
    if (profile.currentStep > 0 && profile.currentStep <= categories.length) {
      const currentCategory = categories[profile.currentStep - 1];
      const categoryKey = currentCategory.name.toLowerCase().replace(/\s+/g, '') as keyof typeof profile.responses;
      const existingResponses = profile.responses[categoryKey];
      
      if (existingResponses && Array.isArray(existingResponses)) {
        setResponses(existingResponses);
      } else {
        setResponses(Array(4).fill(''));
      }
    }
  }, [profile.currentStep, profile.responses]);

  // Timeout for loading state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-lime-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Main Profile Dashboard View
  if (currentView === 'profile') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-lime-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to Your Student Dashboard
            </h1>
            <p className="text-lg text-gray-600">
              Manage your profile, enroll in classes, and take learning surveys
            </p>
          </motion.div>

          {/* Navigation Menu */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer"
              onClick={() => setShowProfileUpdate(true)}
            >
              <div className="text-center">
                <div className="text-4xl mb-4">👤</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Update Profile
                </h3>
                <p className="text-gray-600">
                  Complete your background information and preferences
                </p>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer"
              onClick={() => setCurrentView('enrollment')}
            >
              <div className="text-center">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Enroll in Classes
                </h3>
                <p className="text-gray-600">
                  Add or manage your class enrollments
                </p>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className={`bg-white rounded-2xl shadow-xl p-6 cursor-pointer ${
                enrolledClasses.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => {
                if (enrolledClasses.length > 0) {
                  navigate('/survey');
                }
              }}
            >
              <div className="text-center">
                <div className="text-4xl mb-4">✨</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Take Survey
                </h3>
                <p className="text-gray-600">
                  Complete learning surveys for your classes
                </p>
                {enrolledClasses.length === 0 && (
                  <p className="text-sm text-orange-600 mt-2">
                    Enroll in classes first
                  </p>
                )}
              </div>
            </motion.div>
          </div>

          {/* Enrolled Classes Summary */}
          {enrolledClasses.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-6"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Your Enrolled Classes
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enrolledClasses.filter(enrollment => enrollment?.class?.id).map((enrollment) => (
                  <div
                    key={enrollment.class.id}
                    className="p-4 bg-emerald-50 rounded-lg border border-emerald-200"
                  >
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {enrollment.class.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      {enrollment.class.code}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      {enrollment.class.term}
                    </p>
                    <p className="text-sm text-gray-600">
                      Prof. {enrollment.professor?.fullName || 'TBA'}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* No Classes Message */}
          {enrolledClasses.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-8 text-center"
            >
              <div className="text-6xl mb-4">🎓</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                No Classes Enrolled Yet
              </h2>
              <p className="text-gray-600 mb-6">
                Start by enrolling in your classes to access surveys and features
              </p>
              <button
                onClick={() => setCurrentView('enrollment')}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Enroll in Classes
              </button>
            </motion.div>
          )}
        </div>

        {/* Profile Update Modal */}
        {showProfileUpdate && (
          <ProfileUpdate
            onClose={() => setShowProfileUpdate(false)}
            onSave={handleProfileUpdateSave}
            enrolledClasses={enrolledClasses}
          />
        )}
      </div>
    );
  }

  // Class Enrollment Management
  if (currentView === 'enrollment') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-lime-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back to Dashboard Button */}
          <button
            onClick={() => setCurrentView('profile')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden p-8"
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-6xl mb-4"
              >
                🎓
              </motion.div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Enroll in Your Classes
              </h2>
              <p className="text-lg text-gray-600">
                Select all the classes you're taking this term. You can always modify your enrollments later.
              </p>
            </div>

            {enrolledClasses.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Currently Enrolled Classes</h3>
                <div className="space-y-3 mb-6">
                  {enrolledClasses.filter(enrollment => enrollment?.class?.id).map((enrollment) => (
                    <div key={enrollment.class.id} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold text-gray-900">{enrollment.class.name}</h4>
                          <p className="text-sm text-gray-600">
                            {enrollment.class.code} • {enrollment.class.term} • Prof. {enrollment.professor.fullName}
                          </p>
                        </div>
                        <span className="text-emerald-600 font-medium">✓ Enrolled</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Available Classes</h3>
            {availableClasses.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No classes available for enrollment at this time.</p>
                <p className="text-sm text-gray-400 mt-2">
                  Available classes count: {availableClasses.length}
                </p>
              </div>
            )}
            <div className="space-y-4 mb-8">
              {availableClasses.map((item) => {
                if (!item?.class?.id) return null;
                const isAlreadyEnrolled = enrolledClasses.some(e => e?.class?.id === item.class.id);
                const isSelected = selectedClassesForEnrollment.includes(item.class.id);
                
                return (
                  <motion.div
                    key={item.class.id}
                    whileHover={{ scale: 1.01 }}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isAlreadyEnrolled
                        ? 'border-emerald-200 bg-emerald-50 opacity-60'
                        : isSelected
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                    onClick={() => {
                      if (!isAlreadyEnrolled) {
                        if (isSelected) {
                          setSelectedClassesForEnrollment(prev => prev.filter(id => id !== item.class.id));
                        } else {
                          setSelectedClassesForEnrollment(prev => [...prev, item.class.id]);
                        }
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {item.class.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-1">
                          Course Code: {item.class.code}
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          Term: {item.class.term}
                        </p>
                        <p className="text-sm text-gray-600">
                          Professor: {item.professor.fullName || 'TBA'}
                        </p>
                      </div>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isAlreadyEnrolled
                          ? 'border-emerald-500 bg-emerald-500'
                          : isSelected
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-gray-300'
                      }`}>
                        {(isSelected || isAlreadyEnrolled) && (
                          <div className="w-2 h-2 bg-white"></div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {enrollmentError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-center">{enrollmentError}</p>
              </div>
            )}

            <div className="flex justify-center">
              <button
                onClick={handleEnrollmentSubmit}
                disabled={selectedClassesForEnrollment.length === 0 || enrollmentLoading}
                className={`px-8 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                  selectedClassesForEnrollment.length > 0 && !enrollmentLoading
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {enrollmentLoading && (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>
                  {enrollmentLoading ? 'Enrolling...' : 'Enroll in Selected Classes'}
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Class Selection for Survey
  if (currentView === 'class-selection') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-lime-50 py-12 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4">
          {/* Back to Dashboard Button */}
          <button
            onClick={() => setCurrentView('profile')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden p-8"
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-6xl mb-4"
              >
                📚
              </motion.div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Select Class for Survey
              </h2>
              <p className="text-lg text-gray-600">
                Choose which class you'd like to take the Learning Color Aura survey for
              </p>
            </div>

            <div className="space-y-4 mb-8">
              {enrolledClasses.filter(enrollment => enrollment?.class?.id).map((enrollment) => (
                <motion.div
                  key={enrollment.class.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedClassForSurvey === enrollment.class.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                  onClick={() => setSelectedClassForSurvey(enrollment.class.id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {enrollment.class.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-1">
                        Course Code: {enrollment.class.code}
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        Term: {enrollment.class.term}
                      </p>
                      <p className="text-sm text-gray-600">
                        Professor: {enrollment.professor.fullName || 'TBA'}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedClassForSurvey === enrollment.class.id
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedClassForSurvey === enrollment.class.id && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleClassSelectionForSurvey}
                disabled={!selectedClassForSurvey}
                className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                  selectedClassForSurvey
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue to Survey
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Step 3: Survey (only shown when currentView === 'survey')
  if (currentView !== 'survey') {
    return null; // This shouldn't happen, but safety check
  }

  if (showSummary) {
    return (
      <AuraSummary 
        responses={profile.responses} 
        onEditSurvey={handleEditSurvey}
        onBackToSelection={handleBackToClassSelection}
      />
    );
  }

  if (showIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-lime-50 py-12 flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="p-8">
              <motion.div
                key={introStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div className="flex justify-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-6xl"
                  >
                    {introSteps[introStep].icon}
                  </motion.div>
                </div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-3xl font-bold text-center text-gray-900"
                >
                  {isEditMode ? "Edit Your Learning Color Aura" : introSteps[introStep].title}
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="text-lg text-center text-gray-600 max-w-2xl mx-auto"
                >
                  {isEditMode ? "You can now review and update your survey responses to refine your Learning Color Aura." : introSteps[introStep].description}
                </motion.p>
              </motion.div>

              <div className="mt-12 flex justify-between items-center">
                <div className="flex space-x-2">
                  {introSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 w-2 rounded-full ${
                        index === introStep ? 'bg-emerald-500' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={handleIntroNext}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  {introStep === introSteps.length - 1 ? "Let's Begin!" : "Next"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const currentCategory = categories[profile.currentStep - 1];

  // Get current class info for display
  const currentClassInfo = enrolledClasses.find(e => e?.class?.id === selectedClassForSurvey);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={handleBackToClassSelection}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Class Selection
        </button>
        {currentClassInfo && (
          <div className="text-right">
            <p className="text-sm text-gray-600">Survey for:</p>
            <p className="font-semibold text-gray-900">{currentClassInfo.class.name}</p>
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {isEditMode ? "Edit Your Learning Profile" : "Student Profile Creation"}
          </h1>
          <p className="text-gray-600">
            {isEditMode 
              ? "Review and update your responses to refine your Learning Color Aura. Your current responses are pre-filled for easy editing."
              : "We're creating your Learning Color Aura to understand your characteristics across multiple categories. This helps us provide a more personalized and effective learning experience tailored to your unique needs and preferences."
            }
          </p>
          <div className={`mt-4 p-4 rounded-lg ${isEditMode ? 'bg-yellow-50' : 'bg-emerald-50'}`}>
            <p className={isEditMode ? 'text-yellow-800' : 'text-emerald-800'}>
              {isEditMode ? "Edit Mode: " : "Progress: "}Step {profile.currentStep} of {categories.length}
            </p>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">{currentCategory.name}</h2>
          <p className="text-gray-600 mb-6">{currentCategory.description}</p>

          <div className="space-y-6">
            {currentCategory.questions.map((question, index) => (
              <div key={index} className="border rounded-lg p-4">
                <p className="text-gray-900 mb-3">{question}</p>
                <div className="grid grid-cols-5 gap-2">
                  {likertOptions.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      className={`p-2 text-sm rounded-md transition-colors ${
                        responses[index] === option
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        const newResponses = [...responses];
                        newResponses[index] = option;
                        setResponses(newResponses);
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
          >
            {profile.currentStep === categories.length ? 'Complete' : 'Next'}
          </button>
        </div>
      </div>
      
      {/* Profile Update Modal */}
      {showProfileUpdate && (
        <ProfileUpdate
          onClose={() => setShowProfileUpdate(false)}
          onSave={handleProfileUpdateSave}
          enrolledClasses={enrolledClasses}
        />
      )}
    </div>
  );
};

export default StudentDashboard;